import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useOrganization } from '@clerk/clerk-react';
import { useApi } from '../lib/api';

interface TeamMember {
  id: string;
  userId: string;
  role: 'manager' | 'member';
  joinedAt: string;
  userName: string | null;
  userEmail: string;
  userAvatar: string | null;
}

interface Team {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  members: TeamMember[];
}

interface OrgMember {
  id: string;
  userId: string;
  role: string;
  userName: string | null;
  userEmail: string;
  userAvatar: string | null;
}

export function TeamDetail() {
  const { teamId } = useParams<{ teamId: string }>();
  const { organization } = useOrganization();
  const { get, post, patch, delete: deleteApi } = useApi();

  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Add member state
  const [showAddModal, setShowAddModal] = useState(false);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<'member' | 'manager'>('member');
  const [addingMember, setAddingMember] = useState(false);
  const [loadingOrgMembers, setLoadingOrgMembers] = useState(false);

  useEffect(() => {
    if (teamId) {
      fetchTeam();
    }
  }, [teamId]);

  async function fetchTeam() {
    try {
      setLoading(true);
      setError(null);
      const response = await get<Team>(`/teams/${teamId}`);
      setTeam(response);
      setEditName(response.name);
      setEditDescription(response.description || '');
    } catch (err) {
      setError('Failed to load team');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchOrgMembers() {
    try {
      setLoadingOrgMembers(true);
      const response = await get<{ members: OrgMember[] }>('/organizations/members');
      // Filter out users already in the team
      const teamUserIds = team?.members.map(m => m.userId) || [];
      const availableMembers = response.members.filter(m => !teamUserIds.includes(m.userId));
      setOrgMembers(availableMembers);
    } catch (err) {
      console.error('Failed to load org members:', err);
    } finally {
      setLoadingOrgMembers(false);
    }
  }

  async function handleSaveEdit() {
    if (!team || !editName.trim()) return;

    try {
      setSaving(true);
      const updated = await patch<Team>(`/teams/${teamId}`, {
        name: editName.trim(),
        description: editDescription.trim() || null,
      });
      setTeam({ ...team, name: updated.name, description: updated.description });
      setEditing(false);
    } catch (err: any) {
      alert(err.message || 'Failed to update team');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddMember(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedUserId) return;

    try {
      setAddingMember(true);
      const newMember = await post<TeamMember>(`/teams/${teamId}/members`, {
        userId: selectedUserId,
        role: selectedRole,
      });
      setTeam(team ? { ...team, members: [...team.members, newMember] } : null);
      setShowAddModal(false);
      setSelectedUserId('');
      setSelectedRole('member');
    } catch (err: any) {
      alert(err.message || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  }

  async function handleChangeRole(userId: string, newRole: 'member' | 'manager') {
    if (!team) return;

    try {
      await patch(`/teams/${teamId}/members/${userId}`, { role: newRole });
      setTeam({
        ...team,
        members: team.members.map(m =>
          m.userId === userId ? { ...m, role: newRole } : m
        ),
      });
    } catch (err: any) {
      alert(err.message || 'Failed to update role');
    }
  }

  async function handleRemoveMember(userId: string, userName: string | null) {
    if (!team) return;
    if (!confirm(`Remove ${userName || 'this user'} from the team?`)) return;

    try {
      await deleteApi(`/teams/${teamId}/members/${userId}`);
      setTeam({
        ...team,
        members: team.members.filter(m => m.userId !== userId),
      });
    } catch (err: any) {
      alert(err.message || 'Failed to remove member');
    }
  }

  function openAddModal() {
    setShowAddModal(true);
    fetchOrgMembers();
  }

  if (!organization) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">Organization context required</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">{error || 'Team not found'}</p>
        <Link to="/settings/teams" className="mt-4 text-green-500 hover:text-green-400">
          Back to Teams
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/settings" className="text-slate-400 hover:text-white">Settings</Link>
        <span className="mx-2 text-slate-600">/</span>
        <Link to="/settings/teams" className="text-slate-400 hover:text-white">Teams</Link>
        <span className="mx-2 text-slate-600">/</span>
        <span className="text-white">{team.name}</span>
      </nav>

      {/* Team header */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        {editing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Team Name</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full max-w-md px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Description</label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={2}
                className="w-full max-w-md px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editName.trim()}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setEditName(team.name);
                  setEditDescription(team.description || '');
                }}
                className="px-4 py-2 text-slate-300 hover:text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">{team.name}</h1>
              {team.description && (
                <p className="mt-2 text-slate-400">{team.description}</p>
              )}
              <p className="mt-2 text-sm text-slate-500">
                Created {new Date(team.createdAt).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={() => setEditing(true)}
              className="px-3 py-1 text-sm text-slate-300 hover:text-white border border-slate-600 rounded-md hover:border-slate-500"
            >
              Edit
            </button>
          </div>
        )}
      </div>

      {/* Members section */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-medium text-white">
            Members ({team.members.length})
          </h2>
          <button
            onClick={openAddModal}
            className="inline-flex items-center px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add Member
          </button>
        </div>

        {team.members.length === 0 ? (
          <div className="px-6 py-8 text-center">
            <UsersIcon className="w-10 h-10 mx-auto text-slate-600 mb-3" />
            <p className="text-slate-400">No members yet</p>
            <button
              onClick={openAddModal}
              className="mt-3 text-green-500 hover:text-green-400 text-sm"
            >
              Add the first member
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-slate-700">
            {team.members.map((member) => (
              <li key={member.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {member.userAvatar ? (
                    <img
                      src={member.userAvatar}
                      alt=""
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                      <span className="text-slate-400 font-medium">
                        {(member.userName || member.userEmail)[0].toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="text-white font-medium">
                      {member.userName || member.userEmail}
                    </p>
                    <p className="text-sm text-slate-400">{member.userEmail}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <select
                    value={member.role}
                    onChange={(e) => handleChangeRole(member.userId, e.target.value as 'member' | 'manager')}
                    className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-sm text-white"
                  >
                    <option value="member">Member</option>
                    <option value="manager">Manager</option>
                  </select>
                  <button
                    onClick={() => handleRemoveMember(member.userId, member.userName)}
                    className="p-1 text-slate-500 hover:text-red-400 rounded"
                    title="Remove from team"
                  >
                    <XIcon className="w-5 h-5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Add Team Member</h2>
            <form onSubmit={handleAddMember}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Select Member
                  </label>
                  {loadingOrgMembers ? (
                    <div className="py-4 text-center text-slate-400">Loading members...</div>
                  ) : orgMembers.length === 0 ? (
                    <div className="py-4 text-center text-slate-400">
                      All organization members are already in this team
                    </div>
                  ) : (
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                      required
                    >
                      <option value="">Select a member...</option>
                      {orgMembers.map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {m.userName || m.userEmail} ({m.role})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Role in Team
                  </label>
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value as 'member' | 'manager')}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                  >
                    <option value="member">Member</option>
                    <option value="manager">Manager</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-500">
                    Managers can add/remove members and view team session data
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-slate-300 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingMember || !selectedUserId || orgMembers.length === 0}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md disabled:opacity-50"
                >
                  {addingMember ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Icons
function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
